import { z } from "zod";
import { medicalQuestionCategorySchema } from "./medical-question";

// TES-292 [J20] — coach↔doctor handoff / escalation system. A structured,
// auditable handoff between clinician roles: one role hands a patient concern
// to another with a reason, priority, optional decision request + deadline, and
// a resolution once closed. Shared contract across api ⟷ admin (the queue +
// create form); no patient PII beyond the free-text context, which a clinician
// authors. Examples: coach→doctor symptom report; doctor→coach "dose change
// affects hunger/energy"; system→role risk-pattern escalation.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

// Who can originate a handoff. `system` is an unattended producer (risk-pattern
// escalation) — it has no employee actor, so createdByEmployeeId stays null.
// A-02: a medical-chat risk signal spotted by an admin is
// escalated MANUALLY to the doctor — the admin originates that handoff.
export const HANDOFF_FROM_ROLES = [
  "coach",
  "doctor",
  "admin",
  "system",
] as const;
export const handoffFromRoleSchema = z.enum(HANDOFF_FROM_ROLES);
export type HandoffFromRole = z.infer<typeof handoffFromRoleSchema>;

// Who a handoff is addressed to — a human clinician role that works the queue.
export const HANDOFF_TO_ROLES = ["coach", "doctor"] as const;
export const handoffToRoleSchema = z.enum(HANDOFF_TO_ROLES);
export type HandoffToRole = z.infer<typeof handoffToRoleSchema>;

export const HANDOFF_PRIORITIES = ["low", "medium", "high", "urgent"] as const;
export const handoffPrioritySchema = z.enum(HANDOFF_PRIORITIES);
export type HandoffPriority = z.infer<typeof handoffPrioritySchema>;

// Lifecycle: sent → acknowledged → in_progress → resolved. `sent` is the birth
// state; `resolved` is terminal.
export const HANDOFF_STATUSES = [
  "sent",
  "acknowledged",
  "in_progress",
  "resolved",
] as const;
export const handoffStatusSchema = z.enum(HANDOFF_STATUSES);
export type HandoffStatus = z.infer<typeof handoffStatusSchema>;

// ---------------------------------------------------------------------------
// Transition table
// ---------------------------------------------------------------------------

// Allowed forward transitions. Linear ladder; a handoff can jump straight from
// `sent` to `resolved` (a quick close without a formal acknowledge/start step)
// and from `acknowledged` to `resolved`, but never regresses.
export const HANDOFF_STATUS_TRANSITIONS: Record<
  HandoffStatus,
  readonly HandoffStatus[]
> = {
  sent: ["acknowledged", "in_progress", "resolved"],
  acknowledged: ["in_progress", "resolved"],
  in_progress: ["resolved"],
  resolved: [],
};

export function canTransitionHandoff(
  from: HandoffStatus,
  to: HandoffStatus,
): boolean {
  return HANDOFF_STATUS_TRANSITIONS[from].includes(to);
}

// The non-terminal (open) states — what the work-queue read surfaces.
export const OPEN_HANDOFF_STATUSES: readonly HandoffStatus[] = [
  "sent",
  "acknowledged",
  "in_progress",
];

// ---------------------------------------------------------------------------
// Schemas + DTOs
// ---------------------------------------------------------------------------

// Create a handoff (POST). `fromRole` is derived server-side from the caller's
// role — the client sends the destination + payload only. Context/requested
// decision are free clinician text; deadline is an ISO timestamp.
export const createHandoffSchema = z.object({
  toRole: handoffToRoleSchema,
  reason: z.string().trim().min(1).max(2000),
  priority: handoffPrioritySchema.default("medium"),
  context: z.string().trim().max(4000).optional(),
  requestedDecision: z.string().trim().max(2000).optional(),
  deadline: z.string().datetime().optional(),
  // TES-986 (Ö-266) — "Skicka till läkare" on one patient message in the
  // coach chat (a shared reflection arrives there as a message too). A
  // coach→doctor handoff carrying it also opens a medical review case with
  // the patient's original text; the handoff is the coach's record, the case
  // is the doctor's work.
  sourceMessageId: z.string().uuid().optional(),
  // TES-1120 — the question's category on a "Skicka till läkare", recorded
  // on the medical review case. Optional: an older admin sends none.
  category: medicalQuestionCategorySchema.optional(),
});
export type CreateHandoffRequest = z.infer<typeof createHandoffSchema>;

// Resolve a handoff (POST :id/resolve). Optional resolution note.
export const resolveHandoffSchema = z.object({
  resolution: z.string().trim().max(4000).optional(),
});
export type ResolveHandoffRequest = z.infer<typeof resolveHandoffSchema>;

// TES-1110 — where a handoff came from, when it points at something outside
// the handoff itself. `support_thread`: escalated from the patient's Ella
// Support thread by staff; `sourceMessageId` names the patient message when
// one message (not the whole thread) was escalated. The patient's text stays
// in the thread and is never copied onto the handoff.
export const HANDOFF_SOURCES = ["support_thread"] as const;
export const handoffSourceSchema = z.enum(HANDOFF_SOURCES);
export type HandoffSource = z.infer<typeof handoffSourceSchema>;

// The queue/read shape returned to the admin. All timestamps ISO strings.
export const handoffDtoSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  fromRole: handoffFromRoleSchema,
  toRole: handoffToRoleSchema,
  reason: z.string(),
  priority: handoffPrioritySchema,
  context: z.string().nullable(),
  requestedDecision: z.string().nullable(),
  deadline: z.string().nullable(),
  status: handoffStatusSchema,
  resolution: z.string().nullable(),
  createdByEmployeeId: z.string().nullable(),
  // TES-1034 (Emelie's point 8) — the sender's name, so the receiver sees who
  // handed it over ("Från: <namn> (<roll>) · <datum>"). Resolved on the
  // listings; optional so the single-row action responses need not carry it.
  createdByDisplayName: z.string().nullable().optional(),
  acknowledgedByEmployeeId: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  resolvedByEmployeeId: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // TES-1110 — null for an ordinary clinician handoff. Optional so callers
  // that build a DTO by hand need not carry them.
  source: handoffSourceSchema.nullable().optional(),
  sourceMessageId: z.string().nullable().optional(),
  // A structured category code (for a support escalation, one of
  // SUPPORT_ESCALATION_CATEGORIES). Not free text: safe in audit metadata.
  reasonCode: z.string().nullable().optional(),
});
export type HandoffDto = z.infer<typeof handoffDtoSchema>;

// ---------------------------------------------------------------------------
// A-02 — chat escalation
// ("Kanoniskt läkarbeslut — medicinsk chatt och eskalering"). A risk signal
// identified in the medical chat
// is escalated manually to the responsible doctor. It is a Handoff to the
// doctor whose context starts with this marker, so the five fields the brief
// asks admin to keep map onto the row without a second model:
//   escalation_status  → status        escalated_at → createdAt
//   escalated_by       → createdByEmployeeId
//   responsible_doctor → resolvedByEmployeeId (the doctor who took it)
//   reviewed_at        → resolvedAt    ("Bedömt av läkare")
// ---------------------------------------------------------------------------
export const CHAT_ESCALATION_MARKER = "chat_escalation";
export const CHAT_ESCALATION_REASON = "Medicinsk risksignal i chatten";

export function chatEscalationContext(
  channel: "doctor" | "coach",
  note: string,
): string {
  const trimmed = note.trim();
  return `${CHAT_ESCALATION_MARKER}:${channel}${trimmed ? `; ${trimmed}` : ""}`;
}

export function isChatEscalation(h: { context: string | null }): boolean {
  return h.context?.startsWith(`${CHAT_ESCALATION_MARKER}:`) ?? false;
}

/** The note Emelie wrote, without the marker prefix. */
export function chatEscalationNote(h: { context: string | null }): string {
  if (!isChatEscalation(h)) return h.context ?? "";
  const i = h.context!.indexOf("; ");
  return i === -1 ? "" : h.context!.slice(i + 2);
}
